import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    siteTimeOutValue: state.context.settings.properties?.siteTimeOut,
    isAuthenticated: state.context.session?.isAuthenticated,
    isRememberMe: state.context.session?.rememberMe,
    isPunchOutSession: getIsPunchOutSession(state),
});

const mapDispatchToProps = {
    loadCurrentCart,
    signOut,
    cancelPunchOut,
};

export interface KeepMeLoginModalStye {
    keepMeLoginModal?: ModalPresentationProps;
    keepMeLoginModalButton?: ButtonPresentationProps;
    bodyTypography?: TypographyPresentationProps;
}

export const keepMeLoginModalStye: KeepMeLoginModalStye = {
    keepMeLoginModal: {
        size: 600,
        cssOverrides: {
            modalBody: css`
                margin: 10px;
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    keepMeLoginModalButton: {
        css: css`
            margin-right: 10px;
        `,
    },
    bodyTypography: {
        css: css`
            font-size: 18px;
        `,
    },
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = keepMeLoginModalStye;

const KeepMeLoginModal = ({
    siteTimeOutValue,
    loadCurrentCart,
    signOut,
    cancelPunchOut,
    isPunchOutSession,
    isAuthenticated,
    isRememberMe,
}: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const lastActiveTimeName = "user-lastActiveTime";
    const localRememberMeName = "user-isLocalRememberMe";
    const thirtySeconds = 30000; // thirty
    const siteTimeoutMinutes: number = Number(siteTimeOutValue) * 1000 * 60 || 900000; // fifteenMinutes
    const showModalMinutes: number = (Number(siteTimeOutValue) - 3) * 1000 * 60 || 720000; // twelveMinutes

    let inactivityInterval: number;
    let isRememberMeFromLocalStorage = false;

    if (typeof window !== "undefined") {
        const tempBoolRememberMeText = localStorage.getItem(localRememberMeName);
        if (tempBoolRememberMeText === "true" || isRememberMe) {
            isRememberMeFromLocalStorage = true;
        }
    }

    useEffect(() => {
        if (isAuthenticated && !isRememberMeFromLocalStorage) {
            trackUserEvents();
        }
    }, []);

    useEffect(() => {
        if (isRememberMe) {
            localStorage.removeItem(localRememberMeName);
        }
    });

    const trackUserEvents = () => {
        if (isRememberMeFromLocalStorage && inactivityInterval) {
            clearInterval(inactivityInterval);
            return;
        }
        if (isRememberMeFromLocalStorage) {
            return;
        }
        if (inactivityInterval) {
            return;
        }

        inactivityInterval = setInterval(startInactivityInterval, thirtySeconds);

        setLastActiveTime();
        document.addEventListener("keypress", setLastActiveTime);
        document.addEventListener("mousedown", setLastActiveTime);
        document.addEventListener("ontouchstart", setLastActiveTime);
    };

    const setLastActiveTime = () => {
        localStorage.setItem(lastActiveTimeName, new Date().toISOString());
    };

    const startInactivityInterval = () => {
        const lastActiveTimeText = localStorage.getItem(lastActiveTimeName);
        const timeDelta = Date.now() - (lastActiveTimeText ? new Date(lastActiveTimeText).getTime() : Date.now());

        if (!lastActiveTimeText) {
            setLastActiveTime();
        }

        if (timeDelta >= siteTimeoutMinutes) {
            signOutUser();
            return;
        }

        if (timeDelta >= showModalMinutes) {
            // loading cart to keep session active until pop-up is open
            loadCurrentCart();
            setIsModalOpen(true);
        }
    };

    const close = () => {
        setIsModalOpen(false);
    };

    const signOutUser = () => {
        if (!isRememberMeFromLocalStorage) {
            isPunchOutSession ? cancelPunchOut() : signOut();
        }
    };

    const keepLoggedIn = () => {
        loadCurrentCart();
        close();
    };

    return (
        <>
            {isAuthenticated && !isRememberMeFromLocalStorage && (
                <Modal
                    isOpen={isModalOpen}
                    handleClose={close}
                    {...styles.keepMeLoginModal}
                    headline="Warning"
                    contentLabel="Warning"
                    closeOnEsc
                >
                    <Typography {...styles.bodyTypography} variant="p">
                        {siteMessage("User_Must_Sign_In_Timeout")}
                    </Typography>
                    <div style={{ textAlign: "right" }}>
                        <Button {...styles.keepMeLoginModalButton} onClick={keepLoggedIn}>
                            Keep me logged in
                        </Button>
                    </div>
                </Modal>
            )}
        </>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(KeepMeLoginModal);
